import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { TASK_STATUSES, TASK_TYPES } from "@wms/domain";
import type { TaskBoard, TaskStatus, TaskType } from "@wms/domain";
import { Alert, App, Badge, Button, Card, Col, Input, Modal, Popconfirm, Row, Select, Space, Statistic, Table, Tag, Typography } from "antd";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, errorMessage } from "../api/client";
import { useAuthStore } from "../auth/store";
import type { Page } from "../api/client";
import { operationOf } from "../components/OperationTag";
import { PageHeader } from "../components/PageHeader";
import { BarcodeButton } from "../components/BarcodeButton";
import { DocumentViewer } from "../components/DocumentViewer";
import { RefLabel, useRefRows } from "../components/RefSelect";

interface TaskRow {
  id: string;
  number: string;
  type: TaskType;
  status: TaskStatus;
  priority: string;
  queue_id: string | null;
  assignee_id: string | null;
  storage_object_id: string | null;
  quantity_plan: string;
  from_location_id: string | null;
  to_location_id: string | null;
  document_type: string | null;
  document_id: string | null;
  problem_code: string | null;
  problem_comment: string | null;
  created_at: string;
}

const ACTIVE: TaskStatus[] = ["ready", "received", "in_progress", "waiting", "problem", "blocked"];
const PRIORITIES = [
  { value: "critical", label: "Критичный" },
  { value: "high", label: "Важный" },
  { value: "normal", label: "Обычный" },
  { value: "low", label: "Низкий" },
  { value: "idle", label: "В свободное время" },
];
const COLOR: Record<string, string> = { neutral: "default", info: "blue", progress: "processing", warning: "orange", success: "green", danger: "red" };

function StatusTag({ status }: { status: TaskStatus }) {
  const info = TASK_STATUSES[status];
  return <Tag color={COLOR[info?.color ?? "neutral"]}>{info?.label ?? status}</Tag>;
}

export function DispatcherPage() {
  const queryClient = useQueryClient();
  const { message } = App.useApp();
  const option = useAuthStore((s) => s.option);
  const can = useAuthStore((s) => s.can);
  const dispatcher = can("tasks.dispatch");
  const [queue, setQueue] = useState<string | undefined>();
  const [status, setStatus] = useState<TaskStatus[]>(ACTIVE);
  const [assignee, setAssignee] = useState<string | undefined>();
  const [selectedIds, setSelectedIds] = useState<React.Key[]>([]);
  // KZ-DOC UI03: a fresh task list opens in the viewer of its stored PDF; the HTML form stays reachable from there
  const [printList, setPrintList] = useState<{ id: string; number: string } | null>(null);
  const [messageTo, setMessageTo] = useState<{ user_id: string; name: string } | null>(null);
  const [messageText, setMessageText] = useState("");
  const users = useRefRows("users");

  const board = useQuery({ queryKey: ["tasks", "board"], queryFn: () => api.get<TaskBoard>("/tasks/board"), refetchInterval: 30000 });
  const filter = useMemo(() => {
    const and: unknown[] = [{ field: "status", op: "in", value: status }];
    if (queue) and.push({ field: "queue_id", op: "eq", value: queue });
    if (assignee) and.push({ field: "assignee_id", op: "eq", value: assignee });
    return { and };
  }, [queue, status, assignee]);
  const tasks = useQuery({
    queryKey: ["tasks", "dispatcher", filter],
    queryFn: () => api.get<Page<TaskRow>>("/tasks", { size: 200, sort: "priority,created_at", filter }),
  });

  const replenish = useMutation({
    mutationFn: () => api.post<{ created: number; short: unknown[] }>("/replenishment/plan", {}),
    onSuccess: (data) => {
      message.success(`Пополнение: задач ${data.created}, дефицит ${data.short.length}`);
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
    },
    onError: (e) => message.error(errorMessage(e)),
  });
  const toList = useMutation({
    mutationFn: () => api.post<{ id: string; number: string }>("/task_lists/from-tasks", { task_ids: selectedIds }),
    onSuccess: (list) => {
      message.success(`Лист задач ${list.number} собран`);
      setSelectedIds([]);
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      setPrintList(list);
    },
    onError: (e) => message.error(errorMessage(e)),
  });
  const bulk = useMutation({
    mutationFn: (body: { action: string; user_id?: string | null; priority?: string; comment?: string }) => api.post<{ done: number; errors: { id: string; error: string }[] }>("/tasks/bulk", { ids: selectedIds, ...body }),
    onSuccess: (r) => {
      message.success(`Выполнено: ${r.done}${r.errors.length ? `, ошибок: ${r.errors.length}` : ""}`);
      if (r.errors.length) message.warning(r.errors.map((e) => e.error).slice(0, 3).join("; "));
      setSelectedIds([]);
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
    },
    onError: (e) => message.error(errorMessage(e)),
  });
  const sendMessage = useMutation({
    mutationFn: () => api.post("/tsd/messages", { user_id: messageTo!.user_id, text: messageText }),
    onSuccess: () => {
      message.success("Сообщение отправлено, исполнитель увидит его на ТСД");
      setMessageTo(null);
      setMessageText("");
    },
    onError: (e) => message.error(errorMessage(e)),
  });
  const act = useMutation({
    mutationFn: ({ id, code, body }: { id: string; code: string; body?: unknown }) => api.post(`/tasks/${id}/${code}`, body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["tasks"] }),
    onError: (e) => message.error(errorMessage(e)),
  });

  const userOptions = (users.data?.items ?? []).map((u) => ({ value: String(u.id), label: String(u.full_name ?? u.username ?? u.id) }));

  return (
    <div>
      <PageHeader
        title="Диспетчер задач"
        subtitle="Очереди, исполнители и задачи в реальном времени"
        tags={<Badge status={board.data ? "success" : "default"} text={board.data ? `подключений: ${board.data.connections}` : "нет данных"} />}
        extra={
          <>
            {can("tasks.plan") && (
              <Button loading={replenish.isPending} onClick={() => replenish.mutate()}>
                Спланировать пополнение
              </Button>
            )}
            {can("tasks.tasks.write") && (
              <Link to="/tasks/new">
                <Button type="primary">Новая задача</Button>
              </Link>
            )}
          </>
        }
      />

      {!dispatcher && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="Табло открыто для просмотра"
          description="Назначать исполнителей, менять приоритет, блокировать и отменять задания может диспетчер. Своё задание берите на экране «Мои задания»."
        />
      )}
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        {(board.data?.queues ?? []).map((q) => {
          const total = Object.values(q.counts).reduce((a, b) => a + b, 0);
          const selected = queue === q.queue_id;
          return (
            <Col key={q.queue_code} xs={24} sm={12} lg={6}>
              <Card size="small" hoverable onClick={() => setQueue(selected ? undefined : (q.queue_id ?? undefined))} style={{ borderColor: selected ? "#1677ff" : undefined }}>
                <Statistic title={q.queue_name} value={total} suffix={q.counts.problem ? <Tag color="red">проблем: {q.counts.problem}</Tag> : undefined} />
                {(q.overdue > 0 || q.stuck > 0) && (
                  <Space size={4} style={{ marginTop: 4 }}>
                    {q.overdue > 0 && <Tag color="volcano">просрочено: {q.overdue}</Tag>}
                    {q.stuck > 0 && <Tag color="gold">зависло: {q.stuck}</Tag>}
                  </Space>
                )}
                <Space size={4} wrap style={{ marginTop: 8 }}>
                  {Object.entries(q.counts).map(([s, n]) => (
                    <Tag key={s} color={COLOR[TASK_STATUSES[s as TaskStatus]?.color ?? "neutral"]}>
                      {TASK_STATUSES[s as TaskStatus]?.label ?? s}: {n}
                    </Tag>
                  ))}
                </Space>
              </Card>
            </Col>
          );
        })}
        <Col xs={24} sm={12} lg={6}>
          <Card size="small" title="Исполнители на смене" styles={{ body: { padding: "8px 12px" } }}>
            {(board.data?.executors ?? []).length === 0 && <Typography.Text type="secondary">Никто не взял задачи</Typography.Text>}
            {(board.data?.executors ?? []).map((e) => (
              <div key={e.user_id} style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                <span>
                  <a onClick={() => setMessageTo({ user_id: e.user_id, name: e.full_name })}>{e.full_name}</a>
                </span>
                <span>
                  {e.current_task ? <Tag>{e.current_task}</Tag> : null}
                  <Typography.Text type="secondary">сделано: {e.done_today}</Typography.Text>
                </span>
              </div>
            ))}
          </Card>
        </Col>
      </Row>

      <Space style={{ marginBottom: 12 }} wrap>
        <Select mode="multiple" value={status} onChange={setStatus} style={{ minWidth: 320 }} placeholder="Статусы" options={Object.entries(TASK_STATUSES).map(([value, info]) => ({ value, label: info.label }))} maxTagCount="responsive" />
        <Select allowClear value={assignee} onChange={setAssignee} style={{ minWidth: 220 }} placeholder="Исполнитель" options={userOptions} showSearch optionFilterProp="label" />
        <Button onClick={() => { setStatus(ACTIVE); setQueue(undefined); setAssignee(undefined); }}>Сбросить</Button>
        {dispatcher && selectedIds.length > 0 && (
          <Space size={4} wrap>
            <Tag color="blue">выбрано: {selectedIds.length}</Tag>
            <Select size="small" placeholder="Назначить…" style={{ width: 200 }} options={userOptions} showSearch optionFilterProp="label" onChange={(user_id) => bulk.mutate({ action: "assign", user_id })} />
            <Select size="small" placeholder="Приоритет…" style={{ width: 160 }} options={PRIORITIES} onChange={(priority) => bulk.mutate({ action: "priority", priority })} />
            <Button size="small" onClick={() => bulk.mutate({ action: "unassign" })}>Снять исполнителя</Button>
            <Button size="small" onClick={() => bulk.mutate({ action: "block" })}>Заблокировать</Button>
            <Button size="small" onClick={() => bulk.mutate({ action: "unblock" })}>Разблокировать</Button>
            {option("use_paper_tasks") && (
              <Button size="small" onClick={() => toList.mutate()} loading={toList.isPending}>В лист задач (печать)</Button>
            )}
            <Popconfirm title={`Отменить ${selectedIds.length} задач?`} okText="Да" cancelText="Нет" onConfirm={() => bulk.mutate({ action: "cancel" })}>
              <Button size="small" danger>Отменить</Button>
            </Popconfirm>
          </Space>
        )}
      </Space>

      <Table<TaskRow>
        rowKey="id"
        size="small"
        rowClassName={(r) => {
          const kind = operationOf(r.type);
          return kind ? `wms-doc wms-doc-${kind}` : "";
        }}
        rowSelection={dispatcher ? { selectedRowKeys: selectedIds, onChange: setSelectedIds } : undefined}
        loading={tasks.isLoading}
        dataSource={tasks.data?.items ?? []}
        pagination={{ pageSize: 50 }}
        columns={[
          { title: "№", dataIndex: "number", width: 110, render: (v: string, r) => <Link to={`/tasks/${r.id}`}>{v}</Link> },
          { title: "Тип", dataIndex: "type", width: 120, render: (v: TaskType) => TASK_TYPES[v] ?? v },
          { title: "Статус", dataIndex: "status", width: 140, render: (v: TaskStatus, r) => (
            <Space size={4}>
              <StatusTag status={v} />
              {r.problem_code && <Typography.Text type="danger">{r.problem_comment ?? r.problem_code}</Typography.Text>}
            </Space>
          ) },
          { title: "Приоритет", dataIndex: "priority", width: 160, render: (v: string, r) =>
            dispatcher ? (
              <Select size="small" value={v} options={PRIORITIES} style={{ width: 150 }} onChange={(priority) => act.mutate({ id: r.id, code: "priority", body: { priority } })} />
            ) : (
              (PRIORITIES.find((o) => o.value === v)?.label ?? v)
            ) },
          { title: "Товар", dataIndex: "storage_object_id", render: (v: string | null) => (v ? <><BarcodeButton entity="storage_objects" id={v} /><RefLabel entity="storage_objects" id={v} /></> : "") },
          { title: "Кол-во", dataIndex: "quantity_plan", width: 90, align: "right" },
          { title: "Откуда", dataIndex: "from_location_id", width: 140, render: (v: string | null) => (v ? <><BarcodeButton entity="locations" id={v} /><RefLabel entity="locations" id={v} /></> : "") },
          { title: "Куда", dataIndex: "to_location_id", width: 140, render: (v: string | null) => (v ? <><BarcodeButton entity="locations" id={v} /><RefLabel entity="locations" id={v} /></> : "") },
          { title: "Исполнитель", dataIndex: "assignee_id", width: 220, render: (v: string | null, r) =>
            dispatcher ? (
              <Select size="small" allowClear value={v ?? undefined} options={userOptions} style={{ width: 200 }} placeholder="не назначен" showSearch optionFilterProp="label" onChange={(user_id) => act.mutate({ id: r.id, code: "assign", body: { user_id: user_id ?? null } })} />
            ) : v ? (
              <RefLabel entity="users" id={v} />
            ) : (
              "не назначен"
            ) },
          { title: "", width: 200, render: (_, r) => (
            <Space size={4}>
              {(r.status === "problem" || r.status === "blocked") && <Button size="small" onClick={() => act.mutate({ id: r.id, code: "resolve" })}>В очередь</Button>}
              <Popconfirm title="Отменить задачу?" okText="Да" cancelText="Нет" onConfirm={() => act.mutate({ id: r.id, code: "cancel" })}>
                <Button size="small" danger>Отменить</Button>
              </Popconfirm>
            </Space>
          ) },
        ]}
      />
      {printList && (
        <DocumentViewer
          open
          onClose={() => setPrintList(null)}
          kind="task_list"
          documentId={printList.id}
          legacyForm="task_list"
          legacyPath={`/print-forms/task_list/${printList.id}`}
          title={`Лист задач ${printList.number}`}
        />
      )}
      <Modal title={messageTo ? `Сообщение для ${messageTo.name}` : ""} open={!!messageTo} onCancel={() => setMessageTo(null)} okText="Отправить" cancelText="Отмена" confirmLoading={sendMessage.isPending} onOk={() => sendMessage.mutate()} okButtonProps={{ disabled: !messageText.trim() }}>
        <Input.TextArea rows={3} maxLength={500} value={messageText} onChange={(e) => setMessageText(e.target.value)} placeholder="Текст появится на терминале перед следующим экраном" />
      </Modal>
    </div>
  );
}
